// src/encounter/lib/note-pieces.ts
//
// PIECES — the controlled parts of a saved phrase (spec: docs/encounter-alignment/
// SPEC-smart-phrases-and-template-filling.md §3, §4.2–4.4, §6). Three kinds tonight:
//   pick   a closed pick-list — single, multi, or multi with EXCLUSIVE options ("none");
//   blank  a free-text answer the clinician types ("unknown" and "not applicable" are answers);
//   chart  a quote of the chart — latest vitals, the allergy list, the active problems, the active
//          medications, a LAB SET from the catalogue (`labs.set:<id>`, lab-set-catalogue.ts), one
//          catalogue test (`labs.<id>`, e.g. `labs.hba1c`) or the latest result of any LOINC-coded
//          test (`labs.latest:<LOINC>`) — with its pull time.
//
// ─── TWO SHAPES, ONE CODEC ───────────────────────────────────────────────────────────────────────
// In the React editor a piece is an atomic inline EMBED (`note-piece-blot.ts`): one cursor stop,
// deleted whole, one undo unit. On the WIRE — the saved `notes` Delta and a phrase's `macroValue` —
// it is a TEXT run carrying the attribute `cps-piece` (a JSON string). Why: flutter_quill 10.7.6
// can break on an unknown embed (it needs an embed builder), but it keeps an unknown attribute as
// ignored metadata and shows the text. So the Flutter PA reads the run as ordinary text.
//
// The run's TEXT is always `pieceDisplay(piece)` — the chosen value, the chart quote, or
// `[not completed: <label>]`. That makes the text a checksum: a run whose text no longer equals the
// display of its own attribute was edited somewhere that does not understand pieces (Flutter), so
// `decodeNoteOps` turns it into plain text and drops the metadata (§6.5) — a mismatch is never
// silently kept as coded.
//
// ─── DEFINITION ≠ OCCURRENCE (R1) ───────────────────────────────────────────────────────────────
// A phrase DEFINITION (in `macroValue`) holds labels, options, the chart query and the blank's
// expectation. It never holds `chosen`, `value` or `snapshot`. Inserting a phrase makes a new
// OCCURRENCE with a fresh id; whatever a definition carried in those fields is discarded, so a
// pick-list always starts unchosen (R3) and a chart piece is resolved against THIS patient now.
//
// Display text is deliberately TIME-ZONE FREE: times are wall-clock strings formatted from their
// digits, so the same run reads identically — and still matches its attribute — on every client.

import type { DeltaOp } from './note-delta';
import { LAB_SET_ID, LOINC_CODE, RESERVED_LAB_WORDS, labSet, labTest } from './lab-set-catalogue';
import { clinicDateD45, clinicDateTimeD45 } from '@/shared/lib/clinic-time';

/** The wire attribute a piece run carries. */
export const PIECE_ATTR = 'cps-piece';
/** The editor-only embed name (never on the wire). */
export const PIECE_EMBED = 'cpsPiece';

export interface PickOption {
  code: string;
  system?: string;
  display: string;
}

export interface PickPiece {
  v: 1;
  t: 'pick';
  id: string;
  label: string;
  multi?: boolean;
  /** Codes that exclude every other choice ("none of these"). */
  exclusive?: string[];
  options: PickOption[];
  /** Occurrence only: the chosen codes. */
  chosen?: string[];
  /** The author allowed "Other…" (free text, not coded). */
  other?: boolean;
}

export interface BlankPiece {
  v: 1;
  t: 'blank';
  id: string;
  label: string;
  expects?: 'text';
  unit?: string;
  /** Occurrence only: what the clinician wrote. */
  value?: string;
}

/** A NAMED chart query (fixed name, fixed meaning). */
type NamedChartQuery = 'vitals.latest' | 'allergies.list' | 'problems.active' | 'medications.active';
/** `labs.set:<id>` — every line of one catalogue lab set (e.g. `labs.set:lipid`, `labs.set:renal`). */
export type LabSetQuery = `labs.set:${string}`;
/** `labs.<id>` — one catalogue test, by its line id (e.g. `labs.hba1c`, `labs.potassium`). */
export type LabTestQuery = `labs.${string}`;
/**
 * `labs.latest:<LOINC>` — the latest current result of one LOINC-coded test (e.g.
 * `labs.latest:4548-4`). The phrase author names it with the piece's `label`.
 */
export type LatestLabQuery = `labs.latest:${string}`;
export type ChartQuery = NamedChartQuery | LabSetQuery | LabTestQuery | LatestLabQuery;

export interface ChartResult {
  sourceId?: string;
  display: string;
  value?: string;
  unit?: string;
  measuredAt?: string;
  recordedBy?: string;
  // ─── A lab result (labs.*): each line is ONE observation from ONE report — never joined. ───────
  /** The analyte as the chart names it (the observation's own display). */
  label?: string;
  /** The observation's LOINC code. */
  code?: string;
  /** The laboratory, named as the chart's Labs tab names it (the request's vendor, else the report's). */
  lab?: string;
  /** The laboratory's own interpretation flag, verbatim — never computed. */
  flag?: string;
  /** When the specimen was collected (the observation's effective time) — never the report date. */
  collectedAt?: string;
  /** Day | Minute | Month: how precise `collectedAt` is (a Month date reads "Sep 2026"). */
  collectedAtPrecision?: string;
  /** When the laboratory issued the report. */
  reportedAt?: string;
  /** The reference range the laboratory printed, as it printed it (carried only when its line says so). */
  range?: string;
  // ─── A vital, when the editor reads the card's history (phrasePieces on) ──────────────────────
  /** The vitals-row key of the reading (e.g. `bp`, `weight`) — selects the card's glyph. */
  kind?: string;
  /** Numeric readings of this vital on file, oldest first, as read at insertion. */
  history?: number[];
}

export interface ChartSnapshot {
  query: ChartQuery;
  patientId?: string;
  encounterId?: string;
  /** Wall-clock `YYYY-MM-DDTHH:mm:ss` of the pull. */
  pulledAt: string;
  status: 'ok' | 'empty' | 'error' | 'loading';
  results: ChartResult[];
  /** A lab set: the catalogue version that defined it when this was pulled. */
  catalogueVersion?: string;
}

export interface ChartPiece {
  v: 1;
  t: 'chart';
  id: string;
  query: ChartQuery;
  label?: string;
  /** Occurrence only. */
  snapshot?: ChartSnapshot;
  /** A Chart readout (`chartReadout`, lib/chart-readout.ts): inserted with a typed backslash and shown as a read-only card under the line's
   *  text. On the wire the same run as any chart piece, so Flutter reads its text. */
  readout?: true;
}

export type Piece = PickPiece | BlankPiece | ChartPiece;

const CHART_NAME: Record<NamedChartQuery, string> = {
  'vitals.latest': 'Vitals',
  'allergies.list': 'Allergies',
  'problems.active': 'Active problems',
  'medications.active': 'Medications',
};
const NAMED_QUERIES = Object.keys(CHART_NAME) as NamedChartQuery[];

/** The LOINC code a `labs.latest:<LOINC>` query names; undefined for any other query. */
export function latestLabCode(query: string): string | undefined {
  const code = query.startsWith('labs.latest:') ? query.slice('labs.latest:'.length) : undefined;
  return code !== undefined && LOINC_CODE.test(code) ? code : undefined;
}

/** The set id a `labs.set:<id>` query names (known to this catalogue or not); undefined otherwise. */
export function labSetId(query: string): string | undefined {
  const id = query.startsWith('labs.set:') ? query.slice('labs.set:'.length) : undefined;
  return id !== undefined && LAB_SET_ID.test(id) ? id : undefined;
}

/** The line id a `labs.<id>` query names (known to this catalogue or not); undefined otherwise. */
export function labTestId(query: string): string | undefined {
  const m = /^labs\.([a-z0-9][a-z0-9-]{0,39})$/.exec(query);
  // `set` and `latest` are the other lab query forms' words, never a test id ("labs.latest" alone is
  // a malformed query, not a test called "latest").
  return m && LAB_SET_ID.test(m[1]) && !RESERVED_LAB_WORDS.has(m[1]) ? m[1] : undefined;
}

/** A lab query of any kind. */
export function isLabQuery(query: string): boolean {
  return labSetId(query) !== undefined || labTestId(query) !== undefined || latestLabCode(query) !== undefined;
}

/**
 * A query this client can carry. A set or test id this catalogue does not know is still a query —
 * a newer catalogue may define it — and resolves as "could not be loaded", never as empty.
 */
export function isChartQuery(query: unknown): query is ChartQuery {
  return typeof query === 'string' && (NAMED_QUERIES.includes(query as NamedChartQuery) || isLabQuery(query));
}

/**
 * What a chart query is called. A named query has a fixed name; a lab set the catalogue's name
 * (unless the author labelled the piece); one LOINC-coded test the piece's label, else "Lab result".
 */
export function chartName(query: ChartQuery, label?: string): string {
  const setId = labSetId(query);
  if (setId !== undefined) return label?.trim() || labSet(setId)?.name || 'Lab results';
  const testId = labTestId(query);
  if (testId !== undefined) return label?.trim() || labTest(testId)?.line.label || 'Lab result';
  if (latestLabCode(query) !== undefined) return label?.trim() || 'Lab result';
  return CHART_NAME[query as NamedChartQuery];
}

// ─── Time, without a time zone ───────────────────────────────────────────────────────────────────

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

// Chart-value times: the clinic's clock in D-45 (src/shared/lib/clinic-time.ts, shared with the Labs tab).
export { CLINIC_TIME_ZONE, zonedInstant } from '@/shared/lib/clinic-time';

/** D-45 date and time on the clinic's clock ("5 Oct 2026, 22:00"); see `clinicDateTimeD45`. */
export function wallClockStamp(iso: string | null | undefined): string | undefined {
  return clinicDateTimeD45(iso);
}

/** The D-45 date alone ("12 Sep 2026") on the clinic's calendar; see `clinicDateD45`. */
export function wallClockDate(iso: string | null | undefined): string | undefined {
  return clinicDateD45(iso);
}

/** The stamp BEFORE D-45 ("Sep 30, 2026 • 10:42a"). Only so that a piece saved then still opens as one. */
function preD45Stamp(iso: string | null | undefined): string | undefined {
  const m = /^(\d{4})-(\d{2})-(\d{2})(?:[T ](\d{2}):(\d{2}))?/.exec(iso ?? '');
  if (!m) return undefined;
  const [, y, mo, d, hh, mm] = m;
  const month = MONTHS[Number(mo) - 1];
  if (!month) return undefined;
  const date = `${month} ${d}, ${y}`;
  if (hh == null || mm == null) return date;
  const h = Number(hh);
  return `${date} • ${h % 12 || 12}:${mm}${h < 12 ? 'a' : 'p'}`;
}

/** Now as a wall-clock ISO string (local digits, no zone) — how a pull is stamped. */
export function wallClockNow(now: Date = new Date()): string {
  const p = (n: number) => String(n).padStart(2, '0');
  return `${now.getFullYear()}-${p(now.getMonth() + 1)}-${p(now.getDate())}T${p(now.getHours())}:${p(now.getMinutes())}:${p(now.getSeconds())}`;
}

// ─── What a piece says ──────────────────────────────────────────────────────────────────────────

export function pieceLabel(p: Piece): string {
  return p.t === 'chart' ? (p.label?.trim() || chartName(p.query)) : p.label;
}

export function notCompleted(p: Piece): string {
  return `[not completed: ${pieceLabel(p)}]`;
}

/** A chart link is RESOLVED only when its read came back — with values (`ok`) or with none
 *  (`empty`). A pull that was still pending, or failed, is unresolved. */
function chartResolved(p: ChartPiece): boolean {
  return p.snapshot?.status === 'ok' || p.snapshot?.status === 'empty';
}

/**
 * Open = still needs the clinician: an unchosen pick-list, an empty blank, and a chart link whose
 * read is pending or failed (every occurrence carries a snapshot, so the snapshot's STATUS decides —
 * a failed allergy read must never count as a finished piece).
 */
export function isPieceOpen(p: Piece): boolean {
  if (p.t === 'pick') return !(p.chosen ?? []).length;
  if (p.t === 'blank') return !(p.value ?? '').trim();
  return !chartResolved(p);
}

/**
 * What a chart piece says ON SCREEN (the chip and its control): the R6 wording — "Allergies could not
 * be loaded", "Allergies: Loading…" — or, once resolved, its display.
 */
export function chartScreenText(p: ChartPiece): string {
  const name = chartName(p.query, p.label);
  if (p.snapshot?.status === 'error') return `${name} could not be loaded`;
  if (p.snapshot?.status === 'loading') return `${name}: Loading…`;
  return pieceDisplay(p);
}

/**
 * The run text of a chart piece. D-45: the "as of" stamp appears ONCE, after the values —
 * "Penicillin — High (as of 5 Oct 2026, 22:00)". `preD45` rebuilds the text a piece was saved with
 * before the ruling ("Allergies (as of Sep 30, 2026 • 10:42a): Penicillin — High"), so that text is
 * still accepted as its checksum on load (`legacyDisplays`); the next save writes the D-45 form.
 */
function chartDisplay(p: ChartPiece, preD45 = false): string {
  const s = p.snapshot;
  if (!s) return notCompleted(p);
  const stamp = preD45 ? preD45Stamp : wallClockStamp;
  const name = chartName(s.query, p.label);
  const asOf = `as of ${stamp(s.pulledAt) ?? 'unknown time'}`;
  switch (s.status) {
    // An unresolved chart link is saved as NOT COMPLETED (R5), never as a finished value; the reason
    // rides along. On screen it reads with the R6 wording (`chartScreenText`).
    case 'loading':
      return `[not completed: ${pieceLabel(p)} — not loaded yet]`;
    case 'error':
      // R6: a failed or partial read is never shown as an empty list.
      return `[not completed: ${pieceLabel(p)} — could not be loaded]`;
    case 'empty':
      // R6: only a read that succeeded and returned nothing. Never "No known allergies".
      // A named query reads in lower case ("No allergies recorded"); a lab query keeps the casing of
      // its set's or author's name ("No CBC results recorded", "No HbA1c results recorded").
      return isLabQuery(s.query) ? `No ${name} results recorded (${asOf})` : `No ${name.toLowerCase()} recorded (${asOf})`;
    default: {
      const measured = s.results.map((r) => r.measuredAt).filter(Boolean).sort().pop();
      const tail = measured ? `, measured ${stamp(measured)}` : '';
      const values = s.results.map((r) => r.display).join('; ');
      return preD45 ? `${name} (${asOf}): ${values}${tail}` : `${values}${tail} (${asOf})`;
    }
  }
}

/** The run text for a piece — what the note, the Flutter PA and the plain-text copy all show. */
export function pieceDisplay(p: Piece): string {
  if (p.t === 'pick') {
    const chosen = new Set(p.chosen ?? []);
    const shown = p.options.filter((o) => chosen.has(o.code)).map((o) => o.display);
    return shown.length ? shown.join(', ') : notCompleted(p);
  }
  if (p.t === 'blank') {
    const value = (p.value ?? '').trim();
    if (!value) return notCompleted(p);
    return p.unit ? `${value} ${p.unit}` : value;
  }
  return chartDisplay(p);
}

// ─── Validation (wire and definitions) ───────────────────────────────────────────────────────────

function isObj(x: unknown): x is Record<string, unknown> {
  return !!x && typeof x === 'object' && !Array.isArray(x);
}

/** Parse + shape-check a `cps-piece` attribute value. Anything else → undefined (becomes text). */
export function parsePiece(raw: unknown): Piece | undefined {
  let data: unknown = raw;
  if (typeof raw === 'string') {
    try {
      data = JSON.parse(raw);
    } catch {
      return undefined;
    }
  }
  if (!isObj(data) || data.v !== 1 || typeof data.id !== 'string') return undefined;
  if (data.t === 'pick') {
    if (typeof data.label !== 'string' || !Array.isArray(data.options) || data.options.length < 1) return undefined;
    const ok = data.options.every((o) => isObj(o) && typeof o.code === 'string' && typeof o.display === 'string');
    return ok ? (data as unknown as PickPiece) : undefined;
  }
  if (data.t === 'blank') return typeof data.label === 'string' ? (data as unknown as BlankPiece) : undefined;
  if (data.t === 'chart') {
    return isChartQuery(data.query) ? (data as unknown as ChartPiece) : undefined;
  }
  return undefined;
}

/** R1: which occurrence-only fields a DEFINITION wrongly carries (empty = a valid definition). */
export function occurrenceFieldsIn(p: Record<string, unknown>): string[] {
  return (['chosen', 'value', 'snapshot'] as const).filter((k) => k in p);
}

/** A definition built from a piece: the same shape with every occurrence-only field removed. */
export function toDefinition(p: Piece): Piece {
  const copy: Record<string, unknown> = { ...p };
  for (const k of ['chosen', 'value', 'snapshot']) delete copy[k];
  return copy as unknown as Piece;
}

// ─── Codec ───────────────────────────────────────────────────────────────────────────────────────

type Attrs = Record<string, unknown>;

function withoutPieceAttr(attrs: Attrs | undefined): Attrs | undefined {
  if (!attrs || !(PIECE_ATTR in attrs)) return attrs;
  const rest = { ...attrs };
  delete rest[PIECE_ATTR];
  return Object.keys(rest).length ? rest : undefined;
}

function textOp(insert: string, attributes: Attrs | undefined): DeltaOp {
  return attributes ? { insert, attributes } : { insert };
}

/** The piece an editor op holds, if it is one. */
export function embeddedPiece(op: DeltaOp): Piece | undefined {
  if (!isObj(op.insert) || !(PIECE_EMBED in op.insert)) return undefined;
  return parsePiece((op.insert as Attrs)[PIECE_EMBED]);
}

/**
 * Earlier run texts a piece may have been SAVED with, still accepted as its checksum on load (the
 * next save writes the current text). Before 2026-10-01 a pending / failed chart link was written
 * as its screen wording ("Allergies: Loading…", "Allergies could not be loaded"); before D-45
 * (2026-10-05) a resolved one carried the old stamp and layout.
 */
function legacyDisplays(p: Piece): string[] {
  if (p.t !== 'chart' || !p.snapshot) return [];
  if (p.snapshot.status === 'loading' || p.snapshot.status === 'error') return [chartScreenText(p)];
  // Before D-45 (5 Oct 2026) a resolved quote read "Allergies (as of Sep 30, 2026 • 10:42a): …".
  return [chartDisplay(p, true)];
}

/** The note being opened: whose record and which visit. */
export interface NoteWhere {
  patientId?: string;
  encounterId?: string;
}

/**
 * Where a chart link's quote came from, as far as this note can tell (contract G5):
 *  - `here`      — its pull names this patient AND this visit, or it states nothing about any record
 *                  (no quote, or a pull that was still loading or failed: "[not completed: …]");
 *  - `elsewhere` — it states something (values, or "none recorded") and its pull names another
 *                  patient or visit, or names none (its provenance cannot be shown);
 *  - `unchecked` — it states something, but the note was opened without knowing its own patient and
 *                  visit, so nothing can be compared yet. Never removed on that basis: the caller
 *                  keeps it and must not save until the note's identity is known.
 * Read off the raw JSON, so a chart kind this client cannot parse is still checked.
 */
type ChartOrigin = 'here' | 'elsewhere' | 'unchecked';

function chartOrigin(raw: unknown, where: NoteWhere): ChartOrigin {
  let data: unknown = raw;
  if (typeof raw === 'string') {
    try {
      data = JSON.parse(raw);
    } catch {
      return 'here'; // not a piece at all — handled as text below
    }
  }
  if (!isObj(data) || data.t !== 'chart' || data.snapshot == null) return 'here';
  const s = isObj(data.snapshot) ? data.snapshot : {};
  if (s.status === 'loading' || s.status === 'error') return 'here';
  if (!where.patientId || !where.encounterId) return 'unchecked';
  return s.patientId === where.patientId && s.encounterId === where.encounterId ? 'here' : 'elsewhere';
}

/**
 * Whether WIRE ops carry a run that may quote the chart: any `cps-piece` run that is not plainly a
 * pick-list or a blank (a chart link of any kind, or an attribute this client cannot read). Such a
 * note can only be decoded once its own patient and visit are known — decoded earlier, an edited or
 * unreadable chart run would turn into plain text and lose the provenance that tells this patient's
 * values from another's (G5).
 */
export function wireMayQuoteChart(wireOps: DeltaOp[]): boolean {
  return wireOps.some((op) => {
    const raw = op.attributes?.[PIECE_ATTR];
    if (raw === undefined) return false;
    let data: unknown = raw;
    if (typeof raw === 'string') {
      try {
        data = JSON.parse(raw);
      } catch {
        return true;
      }
    }
    return !(isObj(data) && (data.t === 'pick' || data.t === 'blank'));
  });
}

/**
 * The chart links in EDITOR ops, by where their quote came from (`chartOrigin`), checked against the
 * note's identity NOW. The editor's Save guard (G5): a quote that cannot be checked yet, or that names
 * another patient or visit, is never written back. Live, not frozen at open, so identity arriving
 * after the editor opened clears the guard without losing the draft.
 */
export function chartOrigins(editorOps: DeltaOp[], where: NoteWhere): { unchecked: number; elsewhere: number } {
  let unchecked = 0;
  let elsewhere = 0;
  for (const op of editorOps) {
    const piece = embeddedPiece(op);
    if (piece?.t !== 'chart') continue;
    const origin = chartOrigin(piece, where);
    if (origin === 'unchecked') unchecked++;
    else if (origin === 'elsewhere') elsewhere++;
  }
  return { unchecked, elsewhere };
}

/**
 * Wire → editor. Consecutive runs sharing one `cps-piece` value are one piece (a client may split a
 * run by formatting part of it). A group whose text equals its display becomes one embed; anything
 * else — unparseable JSON, an unknown kind, text edited elsewhere — becomes plain text with the
 * attribute dropped, and is counted in `converted`.
 *
 * TWO GUARDS FOR A NOTE PASTED TOGETHER ELSEWHERE (contract G5). Flutter's clipboard keeps the
 * `cps-piece` attribute, so a piece copied in one patient's note can be pasted into another's:
 *  - a chart link whose quote was pulled for another patient or another visit is REMOVED — the
 *    piece AND its text, because the text is that record's value (a blood pressure, an allergy
 *    list); kept as plain text it would still put another patient's data in this note. Counted in
 *    `removed`, so the editor can say so.
 *  - a second occurrence of the same piece id (a paste of a piece already in the note) is plain
 *    text: one id is one occurrence. Counted in `converted`.
 * Opened WITHOUT its own patient and visit (`where` incomplete), a chart link that states something
 * cannot be checked: it is kept as it is and counted in `unchecked`, and the editor must not save —
 * removing it on an unknown identity would delete this patient's own values.
 */
export function decodeNoteOps(
  ops: DeltaOp[],
  where: NoteWhere = {},
): { ops: DeltaOp[]; converted: number; removed: number; unchecked: number } {
  const out: DeltaOp[] = [];
  let converted = 0;
  let removed = 0;
  let unchecked = 0;
  const seenIds = new Set<string>();
  for (let i = 0; i < ops.length; i++) {
    const op = ops[i];
    const raw = op.attributes?.[PIECE_ATTR];
    if (raw === undefined || typeof op.insert !== 'string') {
      out.push(op);
      continue;
    }
    const group: DeltaOp[] = [op];
    while (
      i + 1 < ops.length &&
      typeof ops[i + 1].insert === 'string' &&
      ops[i + 1].attributes?.[PIECE_ATTR] === raw
    ) {
      group.push(ops[++i]);
    }
    // Another record's chart quote never enters this note — checked before the text, because an
    // edited copy is still that record's value.
    const origin = chartOrigin(raw, where);
    if (origin === 'elsewhere') {
      removed++;
      continue;
    }
    if (origin === 'unchecked') unchecked++;
    const text = group.map((g) => g.insert as string).join('');
    const piece = parsePiece(raw);
    const firstOfId = !!piece && !seenIds.has(piece.id);
    if (piece) seenIds.add(piece.id);
    if (piece && firstOfId && (text === pieceDisplay(piece) || legacyDisplays(piece).includes(text))) {
      const attributes = withoutPieceAttr(op.attributes);
      out.push(attributes ? { insert: { [PIECE_EMBED]: piece }, attributes } : { insert: { [PIECE_EMBED]: piece } });
    } else {
      converted++;
      for (const g of group) out.push(textOp(g.insert as string, withoutPieceAttr(g.attributes)));
    }
  }
  return { ops: out, converted, removed, unchecked };
}

/** Editor → wire. Every piece embed becomes its display text carrying the `cps-piece` attribute. */
export function encodeNoteOps(ops: DeltaOp[]): DeltaOp[] {
  const out: DeltaOp[] = [];
  for (const op of ops) {
    const isPieceEmbed = isObj(op.insert) && PIECE_EMBED in op.insert;
    if (!isPieceEmbed) {
      out.push(op);
      continue;
    }
    const piece = embeddedPiece(op);
    // An embed that no longer parses is dropped rather than written: a piece embed must NEVER
    // reach the wire (flutter_quill throws on an unknown embed).
    if (piece) out.push({ insert: pieceDisplay(piece), attributes: { ...(op.attributes ?? {}), [PIECE_ATTR]: JSON.stringify(piece) } });
  }
  return out;
}

/**
 * The plain-text copy (`notePlainText`, `macroValueFormatted`) of WIRE ops: pieces as their display
 * (open ones as `[not completed: <label>]`, R5), checklist lines as `[x]` / `[ ]`, images as
 * `[image]`, any other embed dropped.
 */
export function exportPlainText(wireOps: DeltaOp[]): string {
  let out = '';
  let line = '';
  for (const op of wireOps) {
    if (typeof op.insert !== 'string') {
      if (isObj(op.insert) && 'image' in op.insert) line += '[image]';
      else {
        const piece = embeddedPiece(op);
        if (piece) line += pieceDisplay(piece);
      }
      continue;
    }
    const parts = op.insert.split('\n');
    parts.forEach((part, idx) => {
      line += part;
      if (idx < parts.length - 1) {
        const list = op.attributes?.list;
        const box = list === 'checked' ? '[x] ' : list === 'unchecked' ? '[ ] ' : '';
        out += `${box}${line}\n`;
        line = '';
      }
    });
  }
  return out + line;
}

// ─── Occurrences ─────────────────────────────────────────────────────────────────────────────────

export function newPieceId(): string {
  return globalThis.crypto?.randomUUID?.() ?? `p-${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;
}

/**
 * A definition → a fresh occurrence (R1). Occurrence-only fields in the definition are DROPPED, so
 * nothing a phrase author saved can pre-answer this visit (R3). A chart piece is resolved here,
 * synchronously, by `resolveChart` — no late write can land after an undo.
 */
export function toOccurrence(def: Piece, resolveChart: (q: ChartQuery) => ChartSnapshot, id = newPieceId()): Piece {
  const base = toDefinition(def);
  if (base.t === 'chart') return { ...base, id, snapshot: resolveChart(base.query) };
  if (base.t === 'pick') return { ...base, id, chosen: [] };
  return { ...base, id };
}

/**
 * Choose `code` on a pick-list. Single: that code. Multi: toggle it — and an EXCLUSIVE code clears
 * every other choice, while any other code clears the exclusive ones (a "none" can never stand
 * next to a positive).
 */
export function choosePick(p: PickPiece, code: string): PickPiece {
  if (!p.options.some((o) => o.code === code)) return p;
  if (!p.multi) return { ...p, chosen: [code] };
  const exclusive = new Set(p.exclusive ?? []);
  const current = p.chosen ?? [];
  if (current.includes(code)) return { ...p, chosen: current.filter((c) => c !== code) };
  if (exclusive.has(code)) return { ...p, chosen: [code] };
  return { ...p, chosen: [...current.filter((c) => !exclusive.has(c)), code] };
}

/**
 * Write what the clinician typed into a blank. An empty (or all-space) value is an explicit CLEAR:
 * the blank goes back to open (`[not completed: <label>]`), with no `value` key left behind.
 */
export function fillBlank(p: BlankPiece, value: string): BlankPiece {
  const v = value.trim();
  if (v) return { ...p, value: v };
  const cleared = { ...p };
  delete cleared.value;
  return cleared;
}
